import { NewsFeedItem } from "@/shared/api/firehose/models";
import { BackendSource, client, searchStories } from "@/shared/api/firehose";
import useFirehoseStore from "@/stores/FirehoseStore";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

const BACKEND_SOURCE = [
  "stocks-one",
  "stocks-two",
  "stocks-three",
  "stocks-four",
  "stocks-four-filings",
] as BackendSource[];

export const useStocksNewsFeed = () => {
  const addTypeStories = useFirehoseStore((state) => state.addTypeStories);
  const stories = useFirehoseStore(
    (state) => state.stocksNewsStories
  ) as NewsFeedItem[];

  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const { data: fetchedStories, isLoading } = useQuery({
    queryKey: ["stocks-news-feed"],
    queryFn: async () => (await client.get("/news/stocks")).data as NewsFeedItem[],
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (fetchedStories) {
      addTypeStories(fetchedStories, ["stocks-news"] as BackendSource[]);
    }
  }, [fetchedStories, addTypeStories]);

  const loadNext = useCallback(async () => {
    setIsLoadingMore(true);
    const nextPage = page + 1;
    setPage(nextPage);

    try {
      const offset = (nextPage - 1) * 50;
      const results = await searchStories({
        limit: 50,
        offset,
        source: BACKEND_SOURCE,
      });

      if (!results || results.length === 0) {
        setHasMore(false);
      } else {
        const combined = [...stories, ...results];
        const unique = combined.filter(
          (story, index, self) =>
            self.findIndex((s) => s.id === story.id) === index
        );
        addTypeStories(unique, ["stocks-news"] as BackendSource[]);
      }
    } catch (error) {
      console.error("Error loading more stocks news:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [page, stories, addTypeStories]);

  return {
    stocksNewsStories: stories,
    isLoading,
    loadNext,
    isLoadingMore,
    hasMore,
  };
};
